import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { AnalyticsTarget } from '~/modules/analytics/analyticsTarget';
import { CoveoEventType } from '~/modules/analytics/coveoEventTypes/coveoEventType';
import { GtmEventType } from '~/modules/analytics/gtmEventTypes/gtmEventType';
import { MarketingAnalyticsEventType } from '~/modules/analytics/marketingAnalyticsEventTypes/marketingAnalyticsEventTypes';
import { IS_DEVELOPMENT } from '~/modules/config';
import { AnalyticsEvent } from '~/modules/services/analyticsService';
import { UserState, userStore } from '~/modules/stores/userStore';

export interface AnalyticsQueueEvent {
    eventType: MarketingAnalyticsEventType | GtmEventType | CoveoEventType;
    event: AnalyticsEvent;
    target: AnalyticsTarget;
}

export interface AnalyticsQueueState extends State {
    analyticsEventsQueue: AnalyticsQueueEvent[];
    isUserProfileLoading: boolean;
}

const initialState = {
    analyticsEventsQueue: [],
    isUserProfileLoading: true,
};

export const analyticsQueueStore = createStore(
    initialState,
    (set: SetState<AnalyticsQueueState>) => ({
        sendAnalyticsEvent(analyticsQueueEvent: AnalyticsQueueEvent) {
            const isUserProfileLoading = analyticsQueueStore.getState().isUserProfileLoading;
            if (isUserProfileLoading) {
                set(function sendAnalyticsEvent(draft) {
                    draft.analyticsEventsQueue.push(analyticsQueueEvent);
                });
            } else {
                analyticsQueueEvent?.target?.sendAnalyticsEvent?.(analyticsQueueEvent);
            }
        },

        sendAllAnalyticsEvents() {
            const analyticsQueueEvents: AnalyticsQueueEvent[] = analyticsQueueStore.getState().analyticsEventsQueue;
            const isUserAuthenticated = !userStore.getState().isGuestUser;
            for (const analyticsQueueEvent of analyticsQueueEvents) {
                const analyticsEventWithAuthContext = {
                    ...analyticsQueueEvent,
                    event: {
                        ...analyticsQueueEvent.event,
                        authenticated: isUserAuthenticated,
                    },
                };
                analyticsQueueEvent?.target?.sendAnalyticsEvent?.(analyticsEventWithAuthContext);
            }
            set(function sendAllAnalyticsEvents(draft) {
                draft.analyticsEventsQueue = [];
            });
        },

        setIsUserProfileLoading(isLoading: boolean) {
            set(function setIsUserProfileLoading(draft) {
                draft.isUserProfileLoading = isLoading;
            });
        },

        reset() {
            set(function reset() {
                return initialState;
            });
        },
    }),
    'analyticsQueueStore',
    IS_DEVELOPMENT,
);

userStore.subscribe(
    (isUserProfileLoading: boolean) => {
        if (!isUserProfileLoading) {
            analyticsQueueStore.sendAllAnalyticsEvents();
        }
        analyticsQueueStore.setIsUserProfileLoading(isUserProfileLoading);
    },
    (state: UserState) => state.isLoading,
);
